import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import {
  AgentSession,
  CreateSessionRequest,
  Message,
  CreateMessageRequest,
} from '../types/session-types';
import type { InternalAgentVisibility } from '@localagent/sdk';

// --- Parameterized hooks (workspaceId passed explicitly) ---
//
// `workspaceId` is `string | null`: a real id targets the workspace-attached
// route family, `null` targets the workspace-less siblings (`/v1/sessions/…`),
// which the SDK's SessionsResource builds from the same optional value.
// A `null` workspace is therefore a VALID
// query state — the `enabled` guards no longer require a workspace.

export const SESSION_LIST_PAGE_SIZE = 100;

// Derived so the ask always exceeds what is rendered — that is what makes
// `length > PAGE_SIZE` exact, with no dependence on the route's default
// (app-shell ADR-012).
export const SESSION_LIST_LIMIT = SESSION_LIST_PAGE_SIZE * 2;

export function useSessions(workspaceId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<AgentSession[], Error>({
    queryKey: ['sessions', workspaceId],
    // On EVERY observer of `['sessions', null]`, so two of them cannot race over
    // which page size wins (app-shell ADR-012).
    queryFn: () =>
      getSDK().sessions.list(
        workspaceId,
        workspaceId === null ? { limit: SESSION_LIST_LIMIT } : undefined,
      ),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useSession(workspaceId: string | null, sessionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<AgentSession, Error>({
    queryKey: ['sessions', workspaceId, sessionId],
    queryFn: () => getSDK().sessions.get(workspaceId, sessionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && sessionId),
  });
}

export function useCreateSession(workspaceId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<AgentSession, Error, CreateSessionRequest>({
    mutationFn: (request) => {
      // The duplicated fabricated groq/openai/gpt-oss-20b fallback is DELETED.
      // Pass agent_config through ONLY
      // when the caller explicitly supplied one; otherwise the session stores no
      // override and the backend resolves provider/model from the persona.
      // `null` workspace persists a workspace-less (account-scoped) session via
      // the SDK's workspace-less create path — no rejection.
      return getSDK().sessions.create(workspaceId, {
        name: request.name,
        ...(request.agentConfig !== undefined
          ? { agent_config: request.agentConfig as unknown as Record<string, unknown> }
          : {}),
        user_config: {
          settings: {
            max_history_messages: 50,
            max_history_tokens: 4000,
          },
        },
        description: `Chat with ${request.personaName}`,
        persona_id: request.personaId || undefined,
        agent_process_id: request.agent_process_id,
        automatic_process_selection: request.automatic_process_selection,
        internal_agent_visibility: request.internal_agent_visibility,
        device_id: request.device_id,
      });
    },
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['sessions', workspaceId] });
      }
    },
  });
}

export function useUpdateSession(workspaceId: string | null, sessionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  // The mutation-variable type is INLINE, so it is the allowlist for what
  // callers may PATCH — a new field must be added here or `updateSession.mutate`
  // is a type error at the call site.
  return useMutation<
    AgentSession,
    Error,
    {
      session_metadata?: Record<string, unknown>;
      persona_id?: string | null;
      name?: string;
      agent_process_id?: string;
      pin_model_to_turn?: boolean;
      automatic_process_selection?: boolean | null;
      internal_agent_visibility?: InternalAgentVisibility;
    }
  >({
    mutationFn: (updates) => {
      if (!sessionId) {
        return Promise.reject(new Error('Session required to update a session'));
      }
      return getSDK().sessions.update(workspaceId, sessionId, updates);
    },
    onSuccess: (updated) => {
      if (!isAuthenticated) return;
      // Mutation-point invalidation call-through (sdk ADR-377c6).
      getSDK().listCache.evictAll();
      getSDK().searchCache.evictAll();
      queryClient.setQueryData<AgentSession>(['sessions', workspaceId, sessionId], (prev) =>
        prev ? { ...prev, ...updated } : updated
      );
      queryClient.setQueryData<AgentSession[]>(['sessions', workspaceId], (prev) =>
        prev?.map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
      );
      void queryClient.invalidateQueries({ queryKey: ['sessions', workspaceId] });
      // The chat sidebar lists the account-scoped lane — a rename must be
      // visible there too, not only on the workspace lane.
      void queryClient.invalidateQueries({ queryKey: ['sessions', null] });
    },
  });
}

export function useDeleteSession(listWorkspaceId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sessionId: string; workspaceId?: string | null }>({
    mutationFn: ({ sessionId, workspaceId }) => {
      const lane = workspaceId !== undefined ? workspaceId : listWorkspaceId;
      return getSDK().sessions.delete(lane, sessionId);
    },
    onSuccess: (_data, { sessionId, workspaceId }) => {
      if (isAuthenticated) {
        // Mutation-point invalidation call-through (sdk ADR-377c6).
        getSDK().listCache.evictAll();
        getSDK().searchCache.evictAll();
        const lane = workspaceId !== undefined ? workspaceId : listWorkspaceId;
        queryClient.removeQueries({ queryKey: ['sessions', lane, sessionId] });
        void queryClient.invalidateQueries({ queryKey: ['sessions', lane], exact: true });
        if (lane !== listWorkspaceId) {
          void queryClient.invalidateQueries({
            queryKey: ['sessions', listWorkspaceId],
            exact: true,
          });
        }
      }
    },
  });
}

export function useCreateMessage(workspaceId: string | null, sessionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<Message, Error, CreateMessageRequest>({
    mutationFn: (request) => {
      if (!sessionId) {
        return Promise.reject(new Error('Session required to create a message'));
      }
      return getSDK().sessions.createMessage(workspaceId, sessionId, {
        role: request.role ?? 'user',
        content: request.content,
      });
    },
    onSuccess: () => {
      if (isAuthenticated && sessionId) {
        void queryClient.invalidateQueries({
          queryKey: ['sessions', workspaceId, sessionId, 'messages'],
        });
      }
    },
  });
}

